<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col>
          <filter-accordion
            :loading="isLoading"
            :query-count="$_queryHandler_numberOfQueries"
            :query-changed="$_queryHandler_hasQueryChanged"
            @clear-filters="$_queryHandler_resetFilters"
          >
            <template #search>
              <search-input v-model="query.searchTerm" clearable hide-details />
            </template>

            <template #default>
              <v-row>
                <v-col>
                  <select-list
                    v-model="query.params.partner"
                    :items="mappedPartners"
                    custom-type="avatar"
                    label="Partner"
                    clearable
                    hide-details
                  />
                </v-col>
                <v-col>
                  <select-list
                    v-model="query.params.type"
                    :items="reportOptions.portals"
                    label="Portal Type"
                    clearable
                    hide-details
                  />
                </v-col>
              </v-row>
            </template>

            <template #content>
              <data-table
                :footer-props="{ 'items-per-page-options': [20, 50, 100, -1] }"
                :headers="headers"
                :items="portals"
                :items-per-page="pagination.perPage"
                :loading="isLoading"
                :page="pagination.currentPage"
                :server-items-length="pagination.total"
                disable-sort
                @pagination="$_queryHandler_onUpdatePagination"
              >
                <template #item.partner="{ item }">
                  <div class="d-flex align-center">
                    <item-logo
                      :src="getPartnerBySlug(item.partner).logoThumbnail"
                      :name="item.partner"
                      class="ml-0 mr-4"
                      size="34"
                    />
                    <span>{{ getPrettyPartnerName(item.partner) }}</span>
                  </div>
                </template>
                <template #actions="{ item }">
                  <v-list-item @click="onClickPreview(item)">
                    <v-list-item-title>
                      <v-icon left small>visibility</v-icon>
                      <span>Preview</span>
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="onClickEdit(item)">
                    <v-list-item-title>
                      <v-icon left small>edit</v-icon>
                      <span>Edit</span>
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="onClickDelete(item)">
                    <v-list-item-title>
                      <v-icon left small>delete</v-icon>
                      <span>Delete</span>
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </data-table>
            </template>
          </filter-accordion>
        </v-col>
      </v-row>
    </v-container>

    <fab-create v-if="isPermittedTo('create')" type="Partner Portal" @click="onClickCreate" />

    <!-- Create/Edit Modal -->
    <generic-modal
      v-if="selectedPortal"
      v-model="showEditModal"
      :is-disabled="isLoading || !isPortalComplete"
      :is-loading="isLoading"
      :title="`${createMode ? 'Create' : 'Edit'} Partner Portal`"
      hide-louis
      persistent
      @modal-confirm="onEditModalConfirm"
      @modal-cancel="onEditModalCancel"
    >
      <select-list v-model="selectedPortal.partner" :items="mappedPartners" label="Partner" required />
      <text-input v-model="selectedPortal.name" label="Name" required />
      <select-list v-model="selectedPortal.type" :items="reportOptions.portals" label="Type" required />
      <text-input v-model="selectedPortal.url" label="URL" required />
      <text-input
        v-model="selectedPortal.username"
        label="Username"
        :error="usernameError"
        :error-messages="usernameErrorMessage"
        @blur="usernameBlurred = true"
      />
      <text-input
        v-model="selectedPortal.email"
        label="Email"
        type="email"
        :error="usernameError"
        :error-messages="usernameErrorMessage"
        @blur="usernameBlurred = true"
      />
      <text-input v-model="selectedPortal.password" label="Password" required />
      <froala-input v-model="selectedPortal.notes" label="Notes" />
    </generic-modal>

    <!-- Preview Modal -->
    <generic-modal
      v-if="selectedPortal"
      v-model="showPreviewModal"
      confirm-text="Okay"
      title="Partner Portal"
      hide-cancel
      hide-louis
      @modal-confirm="onPreviewConfirm"
      @modal-cancel="onPreviewConfirm"
    >
      <table class="report-details">
        <tr>
          <th>Partner</th>
          <td>{{ getPrettyPartnerName(selectedPortal.partner) }}</td>
        </tr>
        <tr>
          <th>Portal Name</th>
          <td>{{ selectedPortal.name }}</td>
        </tr>
        <tr>
          <th>Portal Type</th>
          <td>{{ selectedPortal.type }}</td>
        </tr>
        <tr>
          <th>URL</th>
          <td class="d-flex">
            <div class="d-inline-block mr-2">
              <copy-to-clipboard label="URL" :copy-value="selectedPortal.url" />
            </div>
            <a :href="selectedPortal.url" class="overflow" target="_blank">{{ selectedPortal.url }}</a>
          </td>
        </tr>
        <tr>
          <th>Username</th>
          <td>
            <div class="d-inline-block mr-2">
              <copy-to-clipboard label="Username" :copy-value="selectedPortal.username" />
            </div>
            {{ selectedPortal.username }}
          </td>
        </tr>
        <tr>
          <th>Email</th>
          <td>
            <div class="d-inline-block mr-2">
              <copy-to-clipboard label="Email" :copy-value="selectedPortal.email" />
            </div>
            {{ selectedPortal.email }}
          </td>
        </tr>
        <tr>
          <th>Password</th>
          <td>
            <div class="d-inline-block mr-2">
              <copy-to-clipboard label="Password" :copy-value="selectedPortal.password" />
            </div>
            {{ selectedPortal.password }}
          </td>
        </tr>
        <tr>
          <th valign="top">Notes</th>
          <td>
            <v-card v-if="selectedPortal.notes" outlined class="mt-2 d-inline-block">
              <v-card-text>
                <!-- eslint-disable-next-line vue/no-v-html -->
                <div v-html="selectedPortal.notes" />
              </v-card-text>
            </v-card>
          </td>
        </tr>
      </table>
    </generic-modal>

    <delete-modal
      v-if="selectedPortal"
      v-model="showDeleteModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      :item="selectedPortal.name"
      category="Partner Portal"
      @modal-confirm="onDeleteConfirm"
      @modal-cancel="selectedPortal = null"
    />
  </page-loader>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'

import CopyToClipboard from '@/components/copy-to-clipboard'
import DataTable from '@/components/data-table'
import DeleteModal from '@/components/modals/delete-modal'
import FabCreate from '@/components/fab-create'
import FilterAccordion from '@/components/filter-accordion'
import FroalaInput from '@/components/forms/froala-input'
import GenericModal from '@/components/modals/generic-modal'
import ItemLogo from '@/components/item-logo'
import PageLoader from '@/components/page-loader'
import SearchInput from '@/components/forms/search-input'
import SelectList from '@/components/forms/select-list'
import TextInput from '@/components/forms/text-input'

import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'

import { partnerPortals, partners } from '@/services/offers'

export default {
  name: 'PartnerPortals',
  components: {
    CopyToClipboard,
    DataTable,
    DeleteModal,
    FabCreate,
    FilterAccordion,
    FroalaInput,
    GenericModal,
    ItemLogo,
    PageLoader,
    SearchInput,
    SelectList,
    TextInput,
  },
  mixins: [PermissionsHandler, QueryParamsHandler],
  data: () => ({
    createMode: false,
    headers: [
      { text: 'Partner', value: 'partner', width: '400' },
      { text: 'Portal Name', value: 'name' },
      { text: 'Portal Type', value: 'type' },
      { text: '', value: 'actions' },
    ],
    isLoading: false,
    partners: [],
    pagination: { perPage: -1 },
    portals: [],
    queriesDefined: ['partner', 'type'],
    selectedPortal: null,
    showPreviewModal: false,
    showEditModal: false,
    showDeleteModal: false,
    usernameBlurred: false,
  }),
  computed: {
    ...mapGetters('general', ['hasPageErrors']),
    ...mapGetters('options', ['formattedOption', 'getItemsForOption', 'hasOptions']),
    isPortalComplete() {
      if (!this.selectedPortal) return false

      const required = ['partner', 'name', 'type', 'url', 'password', ['username', 'email']]
      return required.every(key => {
        if (Array.isArray(key)) {
          return key.some(orKey => Boolean(this.selectedPortal[orKey]))
        } else {
          return Boolean(this.selectedPortal[key])
        }
      })
    },
    mappedPartners() {
      return this.partners.map(({ name, slug, logoThumbnail }) => ({
        key: name || slug,
        value: slug,
        icon: logoThumbnail,
      }))
    },
    reportOptions() {
      return this.getItemsForOption('reports')
    },
    usernameError() {
      const { username } = this.selectedPortal
      const { email } = this.selectedPortal
      return this.usernameBlurred && !username && !email
    },
    usernameErrorMessage() {
      const { username } = this.selectedPortal
      const { email } = this.selectedPortal
      return (this.usernameBlurred && !username && !email && 'Either Username or Email must be filled!') || ''
    },
  },
  async created() {
    await this.getPortals()
    await this.getPartners()
    this.setPageLoading(false)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    async getPartners() {
      this.isLoading = true
      try {
        const { data } = await partners.getAllPartners({ compact: true, filters: { archivedContains: [true, false] } })
        this.partners = data
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve Partners',
        })
      }
      this.isLoading = false
    },
    async getPortals() {
      this.isLoading = true
      try {
        const { perPage, currentPage: page } = this.pagination
        const { searchTerm: search } = this.query
        const { partner, type } = this.query.params
        const { data, pagination: { total } = {} } = await partnerPortals.getFilteredPortals({
          filters: { partner, type },
          search,
          perPage: perPage === -1 ? null : perPage,
          page: perPage === -1 ? 1 : page,
        })
        this.pagination.total = total
        this.portals = data
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve Partner Portals',
        })
      }
      this.isLoading = false
    },
    getPrettyPartnerName(partner) {
      const match = this.partners.find(({ slug }) => slug === partner) || {}
      return match.name || `${partner} (Partner does not exist)`
    },
    getPartnerBySlug(slug) {
      return this.partners.find(p => p.slug === slug) || {}
    },
    handleQueryUpdate() {
      this.getPortals()
    },
    onClickCreate() {
      this.selectedPortal = {
        partner: null,
        name: null,
        type: null,
        url: null,
        username: null,
        email: null,
        password: null,
      }
      this.createMode = true
      this.showEditModal = true
    },
    async onCreateConfirm() {
      this.isLoading = true

      try {
        await partnerPortals.createPortal(this.selectedPortal)
        this.$notify({
          type: 'success',
          text: 'Successfully created new Partner Portal',
        })
        await this.getPortals()
        this.showEditModal = false
        this.createMode = false
        this.selectedPortal = null
      } catch (err) {
        this.$notify({
          type: 'error',
          text: 'Unable to create Partner Portal',
        })
      }

      this.usernameBlurred = false
      this.isLoading = false
    },
    onClickPreview(portal) {
      this.selectedPortal = portal
      this.showPreviewModal = true
    },
    onPreviewConfirm() {
      this.selectedPortal = null
      this.showPreviewModal = false
    },
    onClickEdit(portal) {
      this.selectedPortal = portal
      this.showEditModal = true
    },
    onEditModalConfirm() {
      if (this.createMode) {
        this.onCreateConfirm()
      } else {
        this.onEditConfirm()
      }
    },
    async onEditConfirm() {
      this.isLoading = true

      try {
        await partnerPortals.updatePortal(this.selectedPortal.id, this.selectedPortal)
        this.$notify({ type: 'success', text: 'Successfully updated portal' })
        await this.getPortals()
        this.showEditModal = false
        this.selectedPortal = null
      } catch (err) {
        this.$notify({
          type: 'error',
          text: 'Unable to update Partner Portal',
        })
      }

      this.usernameBlurred = false
      this.isLoading = false
    },
    onClickDelete(portal) {
      this.showDeleteModal = true
      this.selectedPortal = portal
    },
    async onDeleteConfirm() {
      this.isLoading = true

      try {
        await partnerPortals.deletePortal(this.selectedPortal.id)
        await this.getPortals()
        this.showDeleteModal = false
        this.selectedPortal = null
      } catch (err) {
        this.$notify({
          type: 'error',
          text: 'Unable to delete Partner Portal',
        })
      }

      this.isLoading = false
    },
    onEditModalCancel() {
      this.selectedPortal = null
      this.usernameBlurred = false
    },
  },
}
</script>

<style lang="scss" scoped>
.report-details {
  width: 100%;

  th {
    text-align: left;
  }
}

.overflow {
  display: inline-block;
  max-width: 350px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
